import { useCallback, useEffect, useReducer, useRef } from 'react';
import socketIo, { Socket } from 'socket.io-client';
import moment from 'moment';
import _ from 'lodash';

import {
  Event,
  GameData,
  MessageBody,
  MessageData,
  MessageHeader,
  ObjectId,
} from 'types';

import { BASE_URL } from 'consts';
import { useRequest } from 'api';
import { useTranslationX } from 'i18n';
import { call } from 'utils';
import { useAppContext } from 'context';

export type LoadMessagesCallback = (callback?: (active: boolean) => void) => void;

type Props = {
  userId: ObjectId;
  events: Event[];
  pageSize?: number;
};

type State = {
  status: 'init' | 'active' | 'error';
  limit: number;
  counter: { [key: ObjectId]: number };
  completed: { [key: ObjectId]: boolean };
  events: ObjectId[];
  hasMessages: boolean;
  game?: GameData;
  messages?: MessageData[];
  error?: { [key: ObjectId]: string };
  loading?: boolean;
  change?: { added?: MessageData[]; removed?: MessageData[] };
};

type Action =
  | { type: 'GAME_UPDATE'; payload: Partial<GameData> }
  | { type: 'ADD_MESSAGE'; payload: EventMessageData }
  | { type: 'REMOVE_MESSAGE'; payload: ObjectId }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOAD_HISTORY'; payload: LoadData }
  | { type: 'CONNECTION_ERROR' };

type EventHistory = { eventId: string } & (
  | { status: 'ok'; game?: GameData; messages?: EventMessageData[] }
  | { status: 'error'; message: string }
);

type EventMessageData = MessageHeader & {
  _id: ObjectId;
  curDate: string;
  profile_pic?: string;
} & MessageBody;

type LoadData = {
  game?: GameData;
  messages?: EventMessageData[];
  error?: { [key: ObjectId]: string };
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'GAME_UPDATE':
      return state.game
        ? { ...state, game: gameUpdate(state.game, action.payload) }
        : state;

    case 'ADD_MESSAGE': {
      const attributes = messagesUpdate(state, action.payload);
      return attributes ? { ...state, ...attributes } : state;
    }

    case 'REMOVE_MESSAGE': {
      const target = state.messages?.find(({ id }) => id !== action.payload);

      return target
        ? {
            ...state,
            messages: state.messages?.filter(el => el !== target),
            change: { removed: [target] },
          }
        : state;
    }

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'LOAD_HISTORY':
      return {
        ...state,
        status: 'active',
        game: action.payload.game || state.game,
        error: action.payload.error,
        loading: false,
        ...messagesUpdate(state, action.payload.messages || []),
      };

    case 'CONNECTION_ERROR':
      return { ...state, status: 'error' };

    default:
      return state;
  }
};

const useEventStateHandler = ({ userId, events, pageSize = 30 }: Props) => {
  const stateRef = useRef<State>({
    status: 'init',
    limit: pageSize,
    counter: {},
    completed: {},
    events: events.map(({ id }) => id),
    hasMessages: true,
  });

  const [, dispatch] = useReducer(
    (state: State, action: Action) => (stateRef.current = reducer(state, action)),
    stateRef.current
  );

  const request = useRequest();

  const { auth } = useAppContext();
  const { tx } = useTranslationX('play.home');

  const loadMessages = useCallback(
    async (callback?: (active: boolean) => void) => {
      const { limit, counter, completed, loading, hasMessages } = stateRef.current;

      if (!hasMessages || loading || !events.length) {
        return call(callback, false);
      }

      call(callback, true);
      dispatch({ type: 'SET_LOADING', payload: true });

      const promises: Promise<EventHistory>[] = [];

      for (const { id: eventId } of events) {
        if (!completed[eventId]) {
          promises.push(
            new Promise<EventHistory>(resolve => {
              request<EventHistory | EventMessageData[]>({
                url: '/webapp/event/history',
                method: 'POST',
                data: { eventId, userId, limit, counter: counter[eventId] },
                onSuccess: data =>
                  resolve(
                    Array.isArray(data)
                      ? { status: 'ok', eventId, messages: data }
                      : { ...data, eventId }
                  ),
                onError: () =>
                  resolve({ status: 'error', eventId, message: tx('error.load') }),
              });
            })
          );
        }
      }

      const results = await Promise.all(promises);
      const messages: EventMessageData[] = [];
      const error: { [key: ObjectId]: string } = {};

      let game: GameData | undefined;

      for (const item of results) {
        if (item.status === 'ok') {
          if (!game && item.game) {
            game = item.game;
          }

          if (item.messages?.length) {
            messages.push(...item.messages);
          }
        } else {
          error[item.eventId] = item.message;
        }
      }

      dispatch({ type: 'LOAD_HISTORY', payload: { game, messages, error } });
    },
    [events, request, tx, userId]
  );

  useEffect(() => {
    if (events.length) {
      const sockets: Socket[] = [];

      for (const { id: eventId } of events) {
        const socket = socketIo(`${BASE_URL}/webapp/user`, {
          transports: ['websocket'],
          query: { join: eventId },
          auth: {
            token: auth.token,
          },
        })
          .on('connectionSuccess', loadMessages)
          .on('connectionError', () => dispatch({ type: 'CONNECTION_ERROR' }))
          .on('changeGame', (payload: Partial<GameData>) =>
            dispatch({ type: 'GAME_UPDATE', payload })
          )
          .on('newMessage', (payload: EventMessageData) =>
            dispatch({ type: 'ADD_MESSAGE', payload })
          )
          .on('messageDeleted', (payload: ObjectId) =>
            dispatch({ type: 'REMOVE_MESSAGE', payload })
          );

        sockets.push(socket);
      }

      return () => {
        for (const socket of sockets) {
          socket.close();
        }
      };
    }
  }, [auth.token, events, loadMessages, userId]);

  return {
    ..._.omit(stateRef.current, 'limit', 'counter', 'completed', 'events'),
    loadMessages,
  };
};

export default useEventStateHandler;

const gameUpdate = (state: GameData, data: Partial<GameData>): GameData => {
  const game = { ...state };

  if (data.homePosition) {
    game.homePosition = data.homePosition;
  }

  if (data.home) {
    game.home = { ...game.home, ...data.home };
  }

  if (data.away) {
    game.away = { ...game.away, ...data.away };
  }

  if (data.period !== undefined) {
    game.period = data.period;
  }

  if (data.clock !== undefined && data.clock !== null) {
    game.clock = data.clock;
  }

  if (data.scheduled !== undefined) {
    game.scheduled = data.scheduled;
  }

  if (data.status !== undefined) {
    game.status = data.status;
  }

  return game;
};

const messagesUpdate = (state: State, data: EventMessageData | EventMessageData[]) => {
  if (!Array.isArray(data)) {
    data = [data];
  }

  if (data.length) {
    const { counter, completed, events } = state;
    const messages = state.messages ? [...state.messages] : [];
    const added = [];

    for (const item of data) {
      const message = parseMessage(item);

      if (!messages.find(({ id }) => id === message.id)) {
        messages.push(message);
        added.push(message);

        const eventCounter = counter[message.event];

        if (eventCounter === undefined || eventCounter > message.counter) {
          counter[message.event] = message.counter;

          if (message.type === 'welcome') {
            completed[message.event] = true;
          }
        }
      }
    }

    if (added.length) {
      let hasMessages = false;

      for (const event of events) {
        if (!completed[event]) {
          hasMessages = true;
          break;
        }
      }

      return {
        messages: sortMessages(messages),
        counter,
        completed,
        hasMessages,
        change: { added },
      };
    }
  }
};

const parseMessage = (message: EventMessageData): MessageData => {
  const { _id, id, curDate, profile_pic, ...rest } = message;

  return {
    ...rest,

    id: _id || id,
    date: moment(curDate),
    avatar: profile_pic,
  };
};

const sortMessages = (messages: MessageData[]): MessageData[] =>
  messages.sort((a, b) =>
    a.date.isAfter(b.date) ? 1 : a.date.isBefore(b.date) ? -1 : 0
  );
